import passwordSchema from "@core/schemas/password";
import usernameSchema from "@core/schemas/username";
import { getGoogleAnalyticsClientId } from "@core/services/google-analytics";
import { recordEventInGtm } from "@core/services/gtm";
import { isApiError } from "@core/services/nocd-api";
import Button from "@core/ui/Button";
import TextField from "@core/ui/TextField";
import { yupResolver } from "@hookform/resolvers/yup";
import cn from "classnames";
import { trim } from "lodash/fp";
import Image from "next/image";
import { signIn } from "next-auth/react";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import OtpInput from "react-otp-input";
import { v4 as uuid } from "uuid";
import * as yup from "yup";

import {
  createUser,
  registerDevice,
  sendOneTimeCodeToNewUser,
} from "../services/nocd-api";

const usernameAndPasswordFormValidationSchema = yup.object({
  email: yup.string().trim().email().required("Enter a valid email address"),
  username: usernameSchema,
  password: passwordSchema,
  oneTimeCode: yup.string().optional(),
});
type RegistrationFormValues = yup.TypeOf<
  typeof usernameAndPasswordFormValidationSchema
>;

interface RegistrationFormProps {
  onSuccess: () => void;
  className?: string;
  email: string;
  source?: string;
  eventToRecord?: string;
  entrancePageUrl?: string;
}

export default function RegistrationForm({
  onSuccess,
  className,
  email,
  source = "member_portal",
  eventToRecord,
  entrancePageUrl,
}: RegistrationFormProps): JSX.Element {
  const [shouldPromptUserForOneTimeCode, setShouldPromptUserForOneTimeCode] =
    useState(false);
  const [isResendingOneTimeCode, setIsResendingOneTimeCode] = useState(false);
  // We store a copy of the email in local state, so we can use it if the
  // user needs us to re-send their one time code.
  const [temporaryEmail, setTemporaryEmail] = useState<string>();

  const form = useForm<RegistrationFormValues>({
    resolver: yupResolver(usernameAndPasswordFormValidationSchema),
    defaultValues: { email },
    mode: "all",
  });

  const { register, formState, control } = form;
  const { isSubmitting, errors } = formState;

  const [sessionId, setSessionId] = useState<string>();
  const [deviceId, setDeviceId] = useState<string>();

  useEffect(() => {
    const getDeviceId = async () => {
      // Generate a unique ID we can use to track the user's session in
      // `nocd_v2.app_opens`.
      const newSessionId = uuid();

      const browserData: Record<string, string> = {};

      // Try to get the user's locale, so we can track it in `nocd_v2.app_opens`.
      try {
        browserData.locale = navigator.language;
      } catch {
        /* ignore */
      }

      // Try to get the user's time zone, so we can track it in
      // `nocd_v2.app_opens`.
      try {
        browserData.timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      } catch {
        /* ignore */
      }

      // Get a device ID we can pass in the `X-DeviceID` header in subsequent
      // requests. We use this to track user registrations and other user events
      // in `nocd_v2.app_opens`.
      return registerDevice({
        sessionId: newSessionId,
        ...browserData,
      }).then(({ deviceID }) => {
        setDeviceId(deviceID);
        setSessionId(newSessionId);
      });
    };

    getDeviceId() as unknown as void;
  }, []);

  const onSubmit = form.handleSubmit(
    async ({
      email: formEmail,
      username,
      password,
      oneTimeCode,
    }: RegistrationFormValues) => {
      const sanitizedEmail = trim(formEmail);

      try {
        const { needsEmailVerification } = await createUser({
          email: sanitizedEmail,
          username,
          password,
          oneTimeCode,
          sessionId,
          deviceId,
          source,
          entrancePageUrl,
        }).then(({ needs_email_verification, email_verification_message }) => ({
          needsEmailVerification: needs_email_verification,
          emailVerificationMessage: email_verification_message,
        }));

        if (needsEmailVerification) {
          setTemporaryEmail(sanitizedEmail);
          return setShouldPromptUserForOneTimeCode(true);
        }

        if (eventToRecord) {
          try {
            recordEventInGtm(eventToRecord);
          } catch (gtmError) {
            console.error(
              "Error recording registration in GTM ",
              gtmError instanceof Error ? gtmError.message : ""
            );
          }
        }

        return await signIn("credentials", {
          redirect: false,
          email: sanitizedEmail,
          password,
          sessionId,
          deviceId,
          gaClientId: getGoogleAnalyticsClientId(),
        }).then(onSuccess);
      } catch (error) {
        let errorMessage: string;

        if (isApiError(error)) {
          errorMessage = error?.response?.data?.message;
        } else if (error instanceof Error) {
          errorMessage = error?.message;
        } else {
          errorMessage = "An unknown error occurred";
        }

        return toast.error(errorMessage);
      }
    }
  );

  return (
    <form onSubmit={onSubmit} className={cn("mx-auto font-manrope", className)}>
      {!shouldPromptUserForOneTimeCode ? (
        <>
          <h2 className="text-center text-22px font-bold tablet:text-24px font-poppins">
            Join us!
          </h2>

          <p className="mt-2 text-center text-16px text-gray-600">
            It&apos;s the first step to getting better.
          </p>

          <div className="space-y-4 mt-4">
            <TextField
              classes={{
                input: "!bg-white !border-2 !border-gray-100",
              }}
              placeholder="Email"
              id="email"
              autoCapitalize="off"
              name="email"
              disabled
              type="text"
              hideLabel
              label="Email"
              autoComplete="email"
              inputMode="email"
              errorMessage={errors.email?.message}
              {...register("email")}
            />

            <TextField
              classes={{
                input: "!bg-white !border-2 !border-gray-100",
              }}
              hideLabel
              placeholder="Password"
              id="password"
              name="password"
              type="password"
              label="Password"
              autoComplete="new-password"
              errorMessage={errors.password?.message}
              {...register("password")}
            />

            <TextField
              classes={{
                input: "!bg-white !border-2 !border-gray-100",
              }}
              hideLabel
              placeholder="Username"
              id="username"
              name="username"
              type="text"
              autoCapitalize="off"
              label="Username"
              autoComplete="username"
              errorMessage={errors.username?.message}
              {...register("username")}
            />
          </div>
        </>
      ) : (
        <>
          <h2 className="text-center text-22px font-bold tablet:text-24px font-poppins">
            Verify your email
          </h2>

          <p className="mt-2 mb-10 text-center text-16px text-gray-600">
            We sent a verification code to your email <strong>{email}</strong>
          </p>

          <Controller
            control={control}
            name="oneTimeCode"
            render={({ field: { onChange, value } }) => (
              <OtpInput
                value={value}
                onChange={onChange}
                numInputs={5}
                renderInput={(props) => <input {...props} />}
                containerStyle={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                  gap: "8px",
                  margin: "40px 0 16pxDidn",
                }}
                inputStyle={{
                  width: "100%",
                  height: "52px",
                  borderRadius: "8px",
                  border: "2px solid #E5E7EB",
                  backgroundColor: "#FFFFFF",
                  textAlign: "center",
                }}
              />
            )}
          />
        </>
      )}

      <div className="mt-10">
        <Button
          type="submit"
          disabled={isSubmitting || !formState?.isValid}
          loading={isSubmitting}
          className="font-semibold h-[52px] w-full disabled:opacity-60 text-18px font-poppins"
          color="indigo"
          rounded="lg"
        >
          {!shouldPromptUserForOneTimeCode
            ? "Create account"
            : "Confirm sign up"}
        </Button>
      </div>

      {shouldPromptUserForOneTimeCode ? (
        <div className="mt-4 text-14px text-center">
          <span className="text-gray-600">Didn&apos;t receive a code? </span>
          <Button
            color="indigo"
            type="button"
            variant="text"
            disabled={isResendingOneTimeCode}
            onClick={() => {
              setIsResendingOneTimeCode(true);

              const promise = sendOneTimeCodeToNewUser(temporaryEmail).then(
                () => setIsResendingOneTimeCode(false)
              );

              // eslint-disable-next-line @typescript-eslint/no-floating-promises
              toast.promise(promise, {
                success: "Code resent",
                loading: "Resending code...",
                error: (error: Error) => error.message,
              });

              return promise;
            }}
          >
            Resend code
          </Button>
        </div>
      ) : null}

      {!shouldPromptUserForOneTimeCode ? (
        <>
          <div className="my-2 text-center text-14px text-gray-800 text-opacity-60">
            By signing up, you agree to NOCD&apos;s{" "}
            <a
              className="text-indigo-600"
              href={`${process.env.NEXT_PUBLIC_WEBSITE_BASE_URL}/terms`}
            >
              Terms &amp; Conditions
            </a>{" "}
            and{" "}
            <a
              className="text-indigo-600"
              href={`${process.env.NEXT_PUBLIC_WEBSITE_BASE_URL}/privacy-policy`}
            >
              Privacy Policy
            </a>
            .
          </div>

          <div className="flex justify-center">
            <Image
              src="/images/authentication-screen/hipaa-logo.png"
              alt="HIPAA Compliant"
              width={121}
              height={29}
            />
          </div>
        </>
      ) : null}
    </form>
  );
}
